import { debounce } from "lodash";
import { useEffect, useState, useRef, useCallback } from "react";

export default function useNearScreen({
  root,
  rootMargin,
  threshold,
  observeOnce = true,
  observeChildren = false,
  debouncedTime = 200
} = {}) {
  const [isNearScreen, setNearScreen] = useState(false);
  const [intersectionRatio, setIntersectionRatio] = useState(0);
  const observerRef = useRef();
  const fromRef = useRef();

  const defaultOnIntersection = useCallback(debounce(entries => {
    const el = entries[0];

    if (el.isIntersecting) {
      setNearScreen(true);
      setIntersectionRatio(el.intersectionRatio);
      if (observeOnce) observerRef.current.disconnect();
    } else if (!observeOnce) {
      setNearScreen(false);
      setIntersectionRatio(el.intersectionRatio);
    }
  }, debouncedTime), [observeOnce]);


  useEffect(() => {
    const element = fromRef.current;
    observerRef.current = new IntersectionObserver(
      defaultOnIntersection,
      {
        root,
        rootMargin,
        threshold
      }
    );
    const observer = observerRef.current;

    if (observer) {
      if (observeChildren && root.children?.length) {
        root.children.forEach(child => observer.observe(child));
      } else if (element) {
        observer.observe(element);
      }
    }

    // eslint-disable-next-line consistent-return
    return () => observer?.disconnect();
  }, [root, rootMargin, threshold, observeChildren, defaultOnIntersection]);

  return [isNearScreen, fromRef, intersectionRatio];
}
